import { ControlledRadioGroup, FieldsetCard, useFormContainerContext, useRatingScale } from '@if/mui'
import { CreditRatingOption } from '@src/types/Constants'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { useTranslation } from 'react-i18next'

// The design system's six rating anchors are green, teal, blue, amber, orange, red;
// the tiles use four of them — green / blue / amber / red.
const RATING_ANCHOR_COUNT = 6
const BAND_ANCHOR_INDEXES = [0, 2, 3, 5] as const

interface CreditRatingSelectorProps {
  options: CreditRatingOption[]
  disabled?: boolean
  /**
   * A promotional payment plan is selected. Its rate overrides the band's server-side
   * (ComputeFunding returns `PromotionInterestRate ?? dto.InterestRate`), so the
   * selection can't affect the estimate and the card is disabled to say so.
   */
  promoPlanSetsRate?: boolean
}

export const CreditRatingSelector = ({
  options,
  disabled = false,
  promoPlanSetsRate = false,
}: CreditRatingSelectorProps) => {
  const { t } = useTranslation()
  const { methods } = useFormContainerContext<PersonalLoanCalculatorSchemaType>()

  const isInert = disabled || promoPlanSetsRate

  // Ask for the six anchors, not one step per tile — otherwise useRatingScale
  // color-mixes a bespoke blend for every band.
  const ratingScale = useRatingScale(RATING_ANCHOR_COUNT)

  // Best-rate-first: green for the best band through red for the worst, with bands
  // close together in rate sharing an anchor. Returns the full RatingStep (a valid
  // SelectionCardTint) for the tile's `tint`. Inert cards drop the rating tint — the
  // group's disabled state greys them, so no tint keeps the rating colour off.
  const tintFor = (index: number, total: number) => {
    if (isInert) return undefined
    const position = total <= 1 ? 0 : Math.round((index * (BAND_ANCHOR_INDEXES.length - 1)) / (total - 1))
    return ratingScale[BAND_ANCHOR_INDEXES[position]]
  }

  // An unbounded band from 0 covers everyone, so it reads as "all ratings" rather
  // than the nonsensical "0 and up".
  const bandRange = (option: CreditRatingOption) => {
    if (option.minErs === 0 && option.maxErs === null) return t('calculator.ersBandAll')
    if (option.maxErs === null) return t('calculator.ersBandOpen', { min: option.minErs })
    return t('calculator.ersBandRange', { min: option.minErs, max: option.maxErs })
  }

  // Rating name above, ERS range below — the rate is deliberately not on the tile.
  const radioOptions = options.map((option, index) => ({
    // Keyed on the band, not the rate — rates repeat when an agreement covers the
    // whole scale, which would leave every tile matching the selection.
    value: option.minErs,
    title: t(option.nameKey),
    description: bandRange(option),
    tint: tintFor(index, options.length),
  }))

  // The rate is resolved server-side now, so the tiles can no longer say where it came from —
  // only that a promotional plan overrides the selection.
  const subtitleKey = promoPlanSetsRate ? 'calculator.creditRatingPromoNote' : undefined

  return (
    <FieldsetCard
      title={t('calculator.creditRatingTitle')}
      subtitle={subtitleKey ? t(subtitleKey) : undefined}
      tooltip={t('calculator.creditRatingHelp')}
    >
      <ControlledRadioGroup
        control={methods.control}
        name="creditRatingMinErs"
        label={t('calculator.creditRatingTitle')}
        hideLabel
        options={radioOptions}
        disabled={isInert}
        variant="tile"
        sx={{
          display: 'grid',
          // The design system dims nothing when disabled, so the group is faded here.
          opacity: isInert ? 0.7 : 1,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      />
    </FieldsetCard>
  )
}
